import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface NavBadges {
  pending_orders: number;
  pending_returns: number;
  low_stock: number;
  support_tickets: number;
  new_bugs: number;
}

/**
 * Whether the tenant has any content behind the nav items that are empty on
 * almost every tenant. Items declaring `hideWhenEmptyKey` are hidden when their
 * key is false — see `AdminNavItem` in config/admin-nav.ts.
 */
export interface NavContent {
  ad_audiences: boolean;
  ad_funnels: boolean;
  ad_cast: boolean;
  ad_videos: boolean;
}

const emptyBadges: NavBadges = {
  pending_orders: 0,
  pending_returns: 0,
  low_stock: 0,
  support_tickets: 0,
  new_bugs: 0,
};

/**
 * Default to "has content" so nothing is hidden before the first response
 * lands — a nav item that vanishes from under the cursor is worse than one
 * that briefly shows an empty page.
 */
const assumePresent: NavContent = {
  ad_audiences: true,
  ad_funnels: true,
  ad_cast: true,
  ad_videos: true,
};

interface NavBadgesResponse {
  badges: NavBadges;
  has?: Partial<NavContent>;
}

const navBadgesQuery = {
  queryKey: ['admin-nav-badges'] as const,
  queryFn: () => api.get<NavBadgesResponse>('/admin/nav-badges'),
  refetchInterval: 60_000,
  staleTime: 30_000,
  gcTime: 5 * 60_000,
};

export function useNavBadges(): NavBadges {
  const { data } = useQuery(navBadgesQuery);
  return data?.badges ?? emptyBadges;
}

export function useNavContent(): NavContent {
  const { data, isSuccess } = useQuery(navBadgesQuery);
  // Memoised on the payload, not rebuilt each render: AdminLayout feeds this
  // into the `visibleSections` memo, and a fresh object identity every render
  // would rebuild the whole sidebar on every render.
  return useMemo(
    () => (isSuccess ? { ...assumePresent, ...(data.has ?? {}) } : assumePresent),
    [isSuccess, data?.has],
  );
}
